import { useState } from "react";
import { FEATURES_CONTENT } from "../data/featuresContent";

// ── Brand tokens ──────────────────────────────────────────────────────────────
const C = {
  evergreen: "#163C2F", forest: "#214D3A", sage: "#7FB98E",
  ivory: "#F5F1EA", brass: "#B08D57", charcoal: "#2B2B2B",
};

// ── Shared micro-components (module-level, stable identity) ───────────────────
function ErrBox({ msg }) {
  return (
    <div style={{ fontSize: 13, color: "#dc2626", background: "#FEF2F2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px" }}>
      {msg}
    </div>
  );
}
// Shared by IdeaModal (nav "Share an Idea" button) and the "Have an idea?"
// page section below — same api/contact.js endpoint and payload shape the
// coming-soon page already uses, so submissions land in the same `ideas`
// table and trigger the same internal-notification/auto-reply emails.
async function submitIdeaToBackend({ name, email, message }) {
  const res = await fetch("/api/contact", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type: "idea", name, email, message }),
  });
  if (!res.ok) throw new Error("Submission failed");
}

function Divider() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
      <div style={{ flex: 1, height: 1, background: "#E8E4DC" }} />
      <span style={{ fontSize: 12, color: "#B0ACA6" }}>or</span>
      <div style={{ flex: 1, height: 1, background: "#E8E4DC" }} />
    </div>
  );
}
function GoogleBtn({ onClick }) {
  return (
    <button onClick={onClick}
      style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "11px 0", background: "#fff", border: "1.5px solid #D4CFC7", borderRadius: 999, cursor: "pointer", fontFamily: "'Manrope',sans-serif", fontSize: 13, fontWeight: 600, color: "#2B2B2B", transition: "background 0.15s" }}
      onMouseEnter={e => e.currentTarget.style.background = "#F9F7F4"}
      onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
      <svg width="18" height="18" viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      </svg>
      Continue with Google
    </button>
  );
}

// ── Sign-in modal (module-level → stable identity, no focus jump) ─────────────
// All form state lives here so typing never triggers a LandingPage re-render.
function SignInModal({ supabase, onClose, initialTab = "landlord" }) {
  const wasInactive = new URLSearchParams(window.location.search).get("reason") === "inactivity";
  const [siTab,   setSiTab]   = useState(initialTab);
  const [mode,    setMode]    = useState("login");
  const [email,   setEmail]   = useState("");
  const [pw,      setPw]      = useState("");
  const [pw2,     setPw2]     = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [loading, setLoading] = useState(false);
  const [authErr, setAuthErr] = useState("");
  const [showPw,  setShowPw]  = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [resetOk,     setResetOk]     = useState(false);
  const [rememberMe,  setRememberMe]  = useState(false);
  const [isBanned,    setIsBanned]    = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [reactivateErr,     setReactivateErr]     = useState("");

  const switchTab = (tab) => { setSiTab(tab); setAuthErr(""); setMode("login"); setResetOk(false); setIsBanned(false); setReactivateErr(""); };

  const signInGoogle = () => {
    // Google OAuth is always treated as "remember me = true"
    localStorage.setItem("leasly_remember_me", "true");
    supabase.auth.signInWithOAuth({
      provider: "google", options: { redirectTo: window.location.origin + "/app" },
    });
  };
  const [noAccount, setNoAccount] = useState(false);
  const signInEmail = async (e) => {
    e.preventDefault(); setAuthErr(""); setNoAccount(false); setIsBanned(false); setReactivateErr(""); setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password: pw });
    setLoading(false);
    if (error) {
      if (error.message.toLowerCase().includes("ban")) {
        setIsBanned(true);
      } else if (error.message === "Invalid login credentials") {
        setNoAccount(true);
      } else {
        setAuthErr(error.message);
      }
      return;
    }
    localStorage.setItem("leasly_remember_me", rememberMe ? "true" : "false");
  };

  const handleReactivate = async () => {
    setReactivateLoading(true);
    setReactivateErr("");
    try {
      const { data, error } = await supabase.functions.invoke("account-self-service", {
        body: { action: "reactivate", email, password: pw },
      });
      if (error || data?.error) {
        setReactivateErr(data?.error || error?.message || "Reactivation failed. Check your password and try again.");
        return;
      }
      if (data?.session) {
        await supabase.auth.setSession({
          access_token:  data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
        setIsBanned(false);
        onClose();
      }
    } catch (err) {
      setReactivateErr(String(err));
    } finally {
      setReactivateLoading(false);
    }
  };
  const sendReset = async (e) => {
    e.preventDefault(); setAuthErr(""); setLoading(true);
    const { data, error: fnErr } = await supabase.functions.invoke("send-password-reset", {
      body: { email, origin: window.location.origin },
    });
    setLoading(false);
    // Treat any non-error response as success (we never reveal whether the email exists)
    if (fnErr) {
      let msg = fnErr.message;
      try { const b = await fnErr.context?.json?.(); if (b?.error) msg = b.error; } catch {}
      setAuthErr(msg);
    } else if (data?.error) {
      setAuthErr(data.error);
    } else {
      setResetOk(true);
    }
  };
  const [signUpOk, setSignUpOk] = useState(false);
  const signUp = async (e) => {
    e.preventDefault(); setAuthErr("");
    if (pw !== pw2) { setAuthErr("Passwords do not match."); return; }
    if (pw.length < 8) { setAuthErr("Password must be at least 8 characters."); return; }
    setLoading(true);
    const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ");
    const { data: signUpData, error } = await supabase.auth.signUp({
      email, password: pw,
      options: {
        data: { full_name: fullName, tier: "free" },
        emailRedirectTo: window.location.origin + "/?confirmed=1",
      },
    });
    setLoading(false);
    if (error) { setAuthErr(error.message); return; }
    localStorage.setItem("leasly_remember_me", "true");
    if (signUpData?.session) {
      setSignUpOk(true);
    } else {
      setAuthErr("");
      setSignUpOk(true);
    }
  };

  const INP = { width: "100%", padding: "11px 14px", fontSize: 14, border: "1px solid #D4CFC7", borderRadius: 8, outline: "none", fontFamily: "'Manrope',sans-serif", background: "#fff", color: C.charcoal, boxSizing: "border-box" };
  const LBL = { fontSize: 12, fontWeight: 600, color: C.charcoal, fontFamily: "'Manrope',sans-serif", marginBottom: 5, display: "block" };
  const btnGreen = (dis) => ({ width: "100%", padding: "13px 0", background: dis ? "#9B9690" : C.evergreen, color: C.ivory, border: "none", borderRadius: 999, fontFamily: "'Manrope',sans-serif", fontSize: 13, fontWeight: 700, cursor: dis ? "not-allowed" : "pointer", textTransform: "uppercase", letterSpacing: "0.08em", transition: "background 0.15s" });
  const EyeToggle = ({ visible, onToggle }) => (
    <button type="button" onClick={onToggle} tabIndex={-1}
      style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, color: "#9B9690", lineHeight: 1 }}>
      {visible
        ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
    </button>
  );

  return (
    <div className="lp-overlay" onClick={onClose}>
      <div className="lp-modal" onClick={e => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 420, padding: "36px 40px", maxHeight: "90vh", overflowY: "auto" }}>
        <button className="lp-modal-close" onClick={onClose}>✕</button>

        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 20 }}>
          <img src="/Leasly-logofinal.png" alt="Leasly" width="28" height="28" style={{ borderRadius: 5, objectFit: "contain" }} />
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 700, color: C.evergreen }}>Leasly</span>
        </div>

        {wasInactive && (
          <div style={{ marginBottom: 18, padding: "10px 14px", background: "#FEF3C7", border: "1px solid #F59E0B44", borderRadius: 8, fontSize: 13, color: "#92400E", fontFamily: "'Manrope',sans-serif", lineHeight: 1.5 }}>
            ⏱ You were signed out due to inactivity. Please sign in again.
          </div>
        )}

        <div style={{ display: "flex", borderBottom: "1px solid #E8E4DC", marginBottom: 24 }}>
          {[["landlord", "Landlord"], ["tenant", "Tenant Portal"]].map(([id, lbl]) => (
            <button key={id} className={`lp-tab${siTab === id ? " active" : ""}`} onClick={() => switchTab(id)}>{lbl}</button>
          ))}
        </div>

        {mode === "signup" ? (
          <>
            <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, fontWeight: 400, color: C.charcoal, marginBottom: 6 }}>Create your account</h2>
            <p style={{ fontSize: 13, color: "#9B9690", marginBottom: 22, lineHeight: 1.6 }}>Start managing your properties for free.</p>
            {signUpOk ? (
              <div style={{ background: C.ivory, border: `1px solid ${C.brass}44`, borderRadius: 12, padding: "28px 24px", textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.evergreen} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline-block" }}>
                    <rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22 4 12 13 2 4" />
                  </svg>
                </div>
                <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 20, fontWeight: 400, color: C.evergreen, marginBottom: 8 }}>Your portfolio is waiting!</div>
                <div style={{ fontSize: 13, color: "#6b7c6b", lineHeight: 1.7 }}>Confirm your email to get started on managing smoother. Check your inbox for a confirmation link from Leasly.</div>
              </div>
            ) : (
            <form onSubmit={signUp} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={LBL}>First name</label>
                  <input type="text" required value={firstName} onChange={e => setFirstName(e.target.value)} style={INP} placeholder="First" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={LBL}>Last name</label>
                  <input type="text" required value={lastName} onChange={e => setLastName(e.target.value)} style={INP} placeholder="Last" />
                </div>
              </div>
              <div>
                <label style={LBL}>Email address</label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} style={INP} placeholder="you@example.com" />
              </div>
              <div>
                <label style={LBL}>Password</label>
                <div style={{ position: "relative" }}>
                  <input type={showPw ? "text" : "password"} required value={pw} onChange={e => setPw(e.target.value)} style={{ ...INP, paddingRight: 40 }} placeholder="At least 8 characters" />
                  <EyeToggle visible={showPw} onToggle={() => setShowPw(v => !v)} />
                </div>
              </div>
              <div>
                <label style={LBL}>Confirm password</label>
                <div style={{ position: "relative" }}>
                  <input type={showPw2 ? "text" : "password"} required value={pw2} onChange={e => setPw2(e.target.value)} style={{ ...INP, paddingRight: 40 }} placeholder="Re-enter password" />
                  <EyeToggle visible={showPw2} onToggle={() => setShowPw2(v => !v)} />
                </div>
              </div>
              {authErr && <ErrBox msg={authErr} />}
              <button type="submit" disabled={loading} style={btnGreen(loading)}>{loading ? "Creating account…" : "Create Account"}</button>
            </form>
            )}
            <button onClick={() => { setMode("login"); setAuthErr(""); setSignUpOk(false); }}
              style={{ marginTop: 16, background: "none", border: "none", color: C.evergreen, fontSize: 13, cursor: "pointer", fontFamily: "'Manrope',sans-serif", fontWeight: 600, padding: 0 }}>
              ← Back to sign in
            </button>
          </>
        ) : mode === "forgot" ? (
          <>
            <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, fontWeight: 400, color: C.charcoal, marginBottom: 6 }}>Reset your password</h2>
            <p style={{ fontSize: 13, color: "#9B9690", marginBottom: 22, lineHeight: 1.6 }}>Enter your email — we'll send a reset link.</p>
            {resetOk ? (
              <div style={{ background: "#F0FDF4", border: "1px solid #86efac", borderRadius: 8, padding: "12px 16px", fontSize: 13, color: "#166534", lineHeight: 1.6 }}>
                ✅ Reset link sent to <strong>{email}</strong>
              </div>
            ) : (
              <form onSubmit={sendReset} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div><label style={LBL}>Email address</label>
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)} style={INP} placeholder="you@example.com" autoFocus />
                </div>
                {authErr && <ErrBox msg={authErr} />}
                <button type="submit" disabled={loading} style={btnGreen(loading)}>{loading ? "Sending…" : "Send Reset Link"}</button>
              </form>
            )}
            <button onClick={() => { setMode("login"); setAuthErr(""); setResetOk(false); }}
              style={{ marginTop: 16, background: "none", border: "none", color: C.evergreen, fontSize: 13, cursor: "pointer", fontFamily: "'Manrope',sans-serif", fontWeight: 600, padding: 0 }}>
              ← Back to sign in
            </button>
          </>
        ) : (
          <>
            <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, fontWeight: 400, color: C.charcoal, marginBottom: 6 }}>
              {siTab === "landlord" ? "Landlord Sign In" : "Tenant Portal"}
            </h2>
            <p style={{ fontSize: 13, color: "#9B9690", marginBottom: 22, lineHeight: 1.6 }}>
              {siTab === "tenant"
                ? "Use the email and password from your landlord's invitation."
                : "Welcome back. Sign in to access your portfolio."}
            </p>
            <form onSubmit={signInEmail} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={LBL}>Email address</label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} style={INP} placeholder="you@example.com" />
              </div>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                  <label style={{ ...LBL, marginBottom: 0 }}>Password</label>
                  <button type="button" onClick={() => { setMode("forgot"); setAuthErr(""); }}
                    style={{ background: "none", border: "none", color: C.evergreen, fontSize: 12, cursor: "pointer", fontFamily: "'Manrope',sans-serif", fontWeight: 600, padding: 0 }}>
                    Forgot password?
                  </button>
                </div>
                <div style={{ position: "relative" }}>
                  <input type={showPw ? "text" : "password"} required value={pw} onChange={e => setPw(e.target.value)} style={{ ...INP, paddingRight: 40 }} placeholder="••••••••" />
                  <EyeToggle visible={showPw} onToggle={() => setShowPw(v => !v)} />
                </div>
              </div>
              {siTab === "landlord" && (
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                    style={{ width: 15, height: 15, accentColor: C.evergreen, cursor: "pointer", flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 13, color: "#6b7c6b", fontFamily: "'Manrope',sans-serif", userSelect: "none" }}>
                    Remember me for 30 days
                  </span>
                </label>
              )}
              {authErr && <ErrBox msg={authErr} />}
              {noAccount && (
                <div style={{ background: "#FEF9EE", border: "1px solid #B08D5744", borderRadius: 8, padding: "14px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 13, color: "#2B2B2B", fontFamily: "'Manrope',sans-serif", marginBottom: 10 }}>
                    No account found with that email. Would you like to create one?
                  </div>
                  <button type="button" onClick={() => { setMode("signup"); setNoAccount(false); setAuthErr(""); setPw(""); }}
                    style={{ ...btnGreen(false), padding: "10px 24px", fontSize: 12, width: "auto" }}>
                    Create Account
                  </button>
                </div>
              )}
              {isBanned && (
                <div style={{ background: "#FEF3C7", border: "1px solid #F59E0B55", borderRadius: 8, padding: "14px 16px" }}>
                  <div style={{ fontSize: 13, color: "#92400E", fontFamily: "'Manrope',sans-serif", fontWeight: 600, marginBottom: 4 }}>
                    Your account is deactivated.
                  </div>
                  <div style={{ fontSize: 12, color: "#92400E", marginBottom: 12, lineHeight: 1.6 }}>
                    Your data is intact. Enter your password above and click below to reactivate.
                  </div>
                  {reactivateErr && (
                    <div style={{ fontSize: 12, color: "#dc2626", marginBottom: 10 }}>{reactivateErr}</div>
                  )}
                  <button
                    type="button"
                    onClick={handleReactivate}
                    disabled={reactivateLoading || !pw}
                    style={{ ...btnGreen(reactivateLoading || !pw), padding: "10px 24px", fontSize: 12, width: "auto", opacity: pw ? 1 : 0.5 }}
                  >
                    {reactivateLoading ? "Reactivating…" : "Reactivate Account"}
                  </button>
                </div>
              )}
              {!isBanned && <button type="submit" disabled={loading} style={btnGreen(loading)}>{loading ? "Signing in…" : "Sign In"}</button>}
            </form>

            {siTab === "landlord" && (
              <>
                <Divider /><GoogleBtn onClick={signInGoogle} />
                <p style={{ textAlign: "center", fontSize: 13, color: "#9B9690", marginTop: 20 }}>
                  New to Leasly?{" "}
                  <button onClick={() => { setMode("signup"); setAuthErr(""); }}
                    style={{ background: "none", border: "none", color: C.evergreen, fontWeight: 700, cursor: "pointer", fontFamily: "'Manrope',sans-serif", fontSize: 13, padding: 0 }}>
                    Start managing here
                  </button>
                </p>
              </>
            )}

            {siTab === "tenant" && (
              <div style={{ marginTop: 16, background: C.ivory, borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#6b7c6b", lineHeight: 1.6 }}>
                💡 Your login was emailed to you by your landlord. Contact them if you need access.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Feature popup (module-level) ──────────────────────────────────────────────
function FeatureModal({ f, onClose, onGetStarted }) {
  return (
    <div className="lp-overlay" onClick={onClose}>
      <div className="lp-modal" onClick={e => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 580, padding: "40px 44px", maxHeight: "90vh", overflowY: "auto" }}>
        <button className="lp-modal-close" onClick={onClose}>✕</button>
        <div style={{ width: 52, height: 52, background: "#EAF2ED", border: "1px solid #C5DBC9", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>{f.icon}</div>
        <h3 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 28, fontWeight: 400, color: C.evergreen, marginBottom: 12 }}>{f.title}</h3>
        <p style={{ fontSize: 14, color: "#4a5e4a", lineHeight: 1.78, marginBottom: 22 }}>{f.intro}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
          {f.bullets.map((b, i) => <div key={i} className="lp-bullet">{b}</div>)}
        </div>
        <div style={{ background: C.ivory, borderRadius: 10, padding: 3, border: "1px solid #E8E4DC", marginBottom: 28 }}>
          <div style={{ borderRadius: 8, overflow: "hidden" }}>{MOCKUPS[f.id]}</div>
        </div>
        <button onClick={onGetStarted}
          style={{ width: "100%", padding: "13px 0", background: C.evergreen, color: C.ivory, border: "none", borderRadius: 999, fontFamily: "'Manrope',sans-serif", fontSize: 13, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.08em", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 10 }}>
          Get Started with Leasly <span style={{ fontSize: 12 }}>✦</span>
        </button>
        <a href={`/features#${f.id}`}
          style={{ width: "100%", padding: "13px 0", background: "transparent", color: C.evergreen, border: `1.5px solid ${C.evergreen}`, borderRadius: 999, fontFamily: "'Manrope',sans-serif", fontSize: 13, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.08em", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, textDecoration: "none", boxSizing: "border-box" }}>
          See full details →
        </a>
      </div>
    </div>
  );
}

// ── All-features modal (module-level) ─────────────────────────────────────────
function FeaturesModal({ onClose, onOpenFeature }) {
  return (
    <div className="lp-overlay" onClick={onClose}>
      <div className="lp-modal" onClick={e => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 780, padding: "44px 44px", maxHeight: "90vh", overflowY: "auto" }}>
        <button className="lp-modal-close" onClick={onClose}>✕</button>
        <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 32, fontWeight: 400, color: C.evergreen, marginBottom: 8 }}>Everything you need</h2>
        <p style={{ fontSize: 14, color: "#9B9690", marginBottom: 32, lineHeight: 1.6 }}>Six core modules built specifically for modern landlords. Click any to learn more.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {FEATURES.map(f => (
            <div key={f.id} onClick={() => onOpenFeature(f.id)}
              style={{ padding: "22px 20px", border: "1px solid #E8E4DC", borderRadius: 10, cursor: "pointer", transition: "background 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = C.ivory; e.currentTarget.style.borderColor = "#C5DBC9"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#E8E4DC"; }}>
              <div style={{ color: C.sage, marginBottom: 12 }}>{f.icon}</div>
              <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 16, color: C.evergreen, marginBottom: 6 }}>{f.title}</div>
              <div style={{ fontSize: 12, color: "#9B9690", lineHeight: 1.6 }}>{f.brief}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── About modal (module-level) ────────────────────────────────────────────────
function AboutModal({ onClose, onGetStarted }) {
  return (
    <div className="lp-overlay" onClick={onClose}>
      <div className="lp-modal" onClick={e => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 520, padding: "44px 44px", maxHeight: "90vh", overflowY: "auto" }}>
        <button className="lp-modal-close" onClick={onClose}>✕</button>
        <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.brass, fontWeight: 600, marginBottom: 16 }}>Our Story</div>
        <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 30, fontWeight: 400, color: C.evergreen, lineHeight: 1.1, marginBottom: 18 }}>
          Built on legacy,<br /><em style={{ color: C.sage }}>powered by technology.</em>
        </h2>
        <p style={{ fontSize: 15, color: "#4a5e4a", lineHeight: 1.85, marginBottom: 16 }}>
          Gibbs Prodigy Group is a real estate property management company built on the belief that smart technology and genuine relationships create better communities.
        </p>
        <p style={{ fontSize: 15, color: "#4a5e4a", lineHeight: 1.85, marginBottom: 16 }}>
          Leasly started as our internal platform — the tool we wished existed when managing our own portfolio. We built it to solve real problems, and now we're making it available to landlords everywhere.
        </p>
        <p style={{ fontSize: 15, color: "#4a5e4a", lineHeight: 1.85, marginBottom: 32 }}>
          Based in Pittsburg, CA. Family-owned. Built to last.
        </p>
        <button onClick={onGetStarted}
          style={{ padding: "13px 32px", background: C.evergreen, color: C.ivory, border: "none", borderRadius: 999, fontFamily: "'Manrope',sans-serif", fontSize: 13, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          Get Started ✦
        </button>
      </div>
    </div>
  );
}

// ── Idea modal (module-level, owns its own form state) ────────────────────────
function IdeaModal({ onClose }) {
  const [form,    setForm]    = useState({ name: "", email: "", message: "" });
  const [done,    setDone]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg,  setErrMsg]  = useState("");

  const INP = { width: "100%", padding: "11px 14px", fontSize: 14, border: "1px solid #D4CFC7", borderRadius: 8, outline: "none", fontFamily: "'Manrope',sans-serif", background: "#fff", color: C.charcoal, boxSizing: "border-box" };
  const LBL = { fontSize: 12, fontWeight: 600, color: C.charcoal, fontFamily: "'Manrope',sans-serif", marginBottom: 5, display: "block" };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErrMsg("");
    try {
      await submitIdeaToBackend(form);
      setDone(true);
    } catch {
      setErrMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lp-overlay" onClick={onClose}>
      <div className="lp-modal" onClick={e => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 480, padding: "44px 44px" }}>
        <button className="lp-modal-close" onClick={onClose}>✕</button>
        <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 30, fontWeight: 400, color: C.evergreen, marginBottom: 10 }}>Have an idea?</h2>
        <p style={{ fontSize: 14, color: "#9B9690", marginBottom: 26, lineHeight: 1.65 }}>Feature request, feedback, or a question — we read everything.</p>
        {done ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, color: C.evergreen, marginBottom: 8 }}>Got it — thank you!</div>
            <div style={{ fontSize: 13, color: "#9B9690", lineHeight: 1.7 }}>We'll get back to you at {form.email}.</div>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div><label style={LBL}>Your name</label><input style={INP} required value={form.name} onChange={e => setForm(s => ({ ...s, name: e.target.value }))} placeholder="Your name" /></div>
            <div><label style={LBL}>Email</label><input type="email" style={INP} required value={form.email} onChange={e => setForm(s => ({ ...s, email: e.target.value }))} placeholder="you@example.com" /></div>
            <div><label style={LBL}>Message</label><textarea style={{ ...INP, resize: "vertical" }} rows={4} required value={form.message} onChange={e => setForm(s => ({ ...s, message: e.target.value }))} placeholder="Tell us your idea…" /></div>
            {errMsg && <ErrBox msg={errMsg} />}
            <button type="submit" disabled={loading}
              style={{ padding: "13px 0", background: loading ? "#9B9690" : C.evergreen, color: C.ivory, border: "none", borderRadius: 999, fontFamily: "'Manrope',sans-serif", fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {loading ? "Sending…" : "Send Message"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Feature data ──────────────────────────────────────────────────────────────
// Copy (title/brief/intro/bullets/icon) lives in src/data/featuresContent.js,
// shared with Features.jsx so the card grid, the popup, and /features can't
// drift apart. Only the decorative popup mockups stay local to this file —
// they're landing-page flourish, not copy, and /features doesn't use them.
const FEATURES = FEATURES_CONTENT;

const MOCKUPS = {
  "tenant-management": (
    <div style={{ background: "#fff", borderRadius: 10, padding: 14, fontSize: 11, fontFamily: "'Manrope',sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #E8E4DC" }}>
        <span style={{ fontWeight: 700, color: C.evergreen, fontSize: 12 }}>Tenant Profiles</span>
        <span style={{ background: "#EAF2ED", color: C.evergreen, padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 600 }}>8 Active</span>
      </div>
      {[{ n: "Marcus Johnson", u: "Apt 2B", r: "$1,850", s: "active" }, { n: "Diana Reeves", u: "Unit 1A", r: "$1,200", s: "expiring" }, { n: "Troy Bennett", u: "Unit 3C", r: "$2,100", s: "active" }].map((t, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid #F5F1EA" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: C.ivory, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: C.evergreen, fontSize: 10 }}>{t.n[0]}</div>
            <div><div style={{ fontWeight: 600, color: C.charcoal }}>{t.n}</div><div style={{ color: "#9B9690", fontSize: 10 }}>{t.u}</div></div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 700, color: C.evergreen, fontSize: 11 }}>{t.r}/mo</div>
            <div style={{ fontSize: 10, color: t.s === "expiring" ? C.brass : C.sage, fontWeight: 600, textTransform: "uppercase" }}>{t.s}</div>
          </div>
        </div>
      ))}
    </div>
  ),
  "rent-payments": (
    <div style={{ background: "#fff", borderRadius: 10, padding: 14, fontSize: 11, fontFamily: "'Manrope',sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #E8E4DC" }}>
        <span style={{ fontWeight: 700, color: C.evergreen, fontSize: 12 }}>June 2026</span>
        <div style={{ display: "flex", gap: 10 }}>
          <span style={{ color: "#22c55e", fontWeight: 600 }}>$9,400 collected</span>
          <span style={{ color: "#dc2626", fontWeight: 600 }}>$1,200 overdue</span>
        </div>
      </div>
      {[{ n: "Marcus Johnson", amt: "$1,850", s: "paid" }, { n: "Diana Reeves", amt: "$1,200", s: "overdue" }, { n: "Troy Bennett", amt: "$2,100", s: "paid" }, { n: "Aaliyah Torres", amt: "$975", s: "paid" }].map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #F5F1EA" }}>
          <span style={{ color: C.charcoal, fontWeight: 500 }}>{p.n}</span>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ fontWeight: 700, color: C.charcoal }}>{p.amt}</span>
            <span style={{ padding: "1px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: p.s === "paid" ? "#EAF2ED" : "#FEF2F2", color: p.s === "paid" ? C.evergreen : "#dc2626" }}>{p.s}</span>
          </div>
        </div>
      ))}
    </div>
  ),
  maintenance: (
    <div style={{ background: "#fff", borderRadius: 10, padding: 14, fontSize: 11, fontFamily: "'Manrope',sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #E8E4DC" }}>
        <span style={{ fontWeight: 700, color: C.evergreen, fontSize: 12 }}>Open Tickets</span>
        <span style={{ background: "#FEF2F2", color: "#dc2626", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 600 }}>4 Open</span>
      </div>
      {[{ issue: "Kitchen faucet leaking", prop: "27 Madison Ave", pri: "high", s: "open" }, { issue: "Broken window latch", prop: "45 Madison Ave", pri: "medium", s: "in progress" }, { issue: "HVAC filter replacement", prop: "155 W Siino Ave", pri: "low", s: "open" }].map((m, i) => (
        <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid #F5F1EA" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
            <span style={{ fontWeight: 600, color: C.charcoal }}>{m.issue}</span>
            <span style={{ padding: "1px 7px", borderRadius: 999, fontSize: 9, fontWeight: 700, textTransform: "uppercase", background: m.pri === "high" ? "#FEF2F2" : m.pri === "medium" ? "#FFFBEB" : C.ivory, color: m.pri === "high" ? "#dc2626" : m.pri === "medium" ? C.brass : "#9B9690" }}>{m.pri}</span>
          </div>
          <div style={{ color: "#9B9690", fontSize: 10 }}>{m.prop} · <span style={{ color: m.s === "in progress" ? C.forest : "#dc2626", fontWeight: 600 }}>{m.s}</span></div>
        </div>
      ))}
    </div>
  ),
  "financial-reports": (
    <div style={{ background: "#fff", borderRadius: 10, padding: 14, fontSize: 11, fontFamily: "'Manrope',sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid #E8E4DC" }}>
        <span style={{ fontWeight: 700, color: C.evergreen, fontSize: 12 }}>Portfolio P&L — 2026</span>
        <span style={{ fontWeight: 600, color: "#22c55e" }}>+$47,820 net</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 4, padding: "4px 0 8px", borderBottom: "1px solid #F5F1EA" }}>
        {["MONTH", "INCOME", "EXPENSES", "NET"].map(h => <span key={h} style={{ color: "#9B9690", fontSize: 10 }}>{h}</span>)}
      </div>
      {[{ m: "March", inc: "$11,200", exp: "$1,840", net: "$9,360" }, { m: "April", inc: "$11,200", exp: "$3,200", net: "$8,000" }, { m: "May", inc: "$11,200", exp: "$1,100", net: "$10,100" }].map((r, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 4, padding: "6px 0", borderBottom: "1px solid #F5F1EA", alignItems: "center" }}>
          <span style={{ fontWeight: 600, color: C.charcoal }}>{r.m}</span>
          <span style={{ color: "#22c55e", fontWeight: 600, textAlign: "right" }}>{r.inc}</span>
          <span style={{ color: "#dc2626", fontWeight: 600, textAlign: "right" }}>{r.exp}</span>
          <span style={{ color: C.evergreen, fontWeight: 700, textAlign: "right" }}>{r.net}</span>
        </div>
      ))}
    </div>
  ),
  documents: (
    <div style={{ background: "#fff", borderRadius: 10, padding: 14, fontSize: 11, fontFamily: "'Manrope',sans-serif" }}>
      <div style={{ fontWeight: 700, color: C.evergreen, fontSize: 12, marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #E8E4DC" }}>Document Templates</div>
      {[{ icon: "📈", name: "Rent Increase Notice", sub: "Auto-fills tenant & new rent amount" }, { icon: "📋", name: "Lease Renewal Offer", sub: "Customizable term and rent fields" }, { icon: "🏠", name: "Move-In Welcome Letter", sub: "Professional first-day welcome" }, { icon: "⚠️", name: "Late Payment Notice", sub: "Firm and professional tone" }].map((d, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid #F5F1EA" }}>
          <span style={{ fontSize: 16 }}>{d.icon}</span>
          <div><div style={{ fontWeight: 600, color: C.charcoal }}>{d.name}</div><div style={{ color: "#9B9690", fontSize: 10 }}>{d.sub}</div></div>
        </div>
      ))}
    </div>
  ),
  alerts: (
    <div style={{ background: "#fff", borderRadius: 10, padding: 14, fontSize: 11, fontFamily: "'Manrope',sans-serif" }}>
      <div style={{ fontWeight: 700, color: C.evergreen, fontSize: 12, marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #E8E4DC" }}>Recent Messages</div>
      {[{ to: "Marcus Johnson", msg: "Your payment receipt for $1,850 is attached.", ch: "Email", time: "Today 9:04am" }, { to: "Diana Reeves", msg: "Your invoice for June rent is ready.", ch: "Email", time: "Yesterday" }, { to: "Troy Bennett", msg: "Receipt for your payment of $2,100.", ch: "Email", time: "Jun 28" }].map((m, i) => (
        <div key={i} style={{ padding: "7px 0", borderBottom: "1px solid #F5F1EA" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
            <span style={{ fontWeight: 600, color: C.charcoal }}>{m.to}</span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ background: "#EFF6FF", color: "#1d4ed8", padding: "1px 6px", borderRadius: 999, fontSize: 9, fontWeight: 600 }}>{m.ch}</span>
              <span style={{ color: "#9B9690", fontSize: 10 }}>{m.time}</span>
            </div>
          </div>
          <div style={{ color: "#9B9690", fontSize: 10, lineHeight: 1.4 }}>{m.msg}</div>
        </div>
      ))}
    </div>
  ),
};

// ── Global CSS ────────────────────────────────────────────────────────────────
const CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { font-family: 'Manrope', sans-serif; background: ${C.ivory}; color: ${C.charcoal}; -webkit-font-smoothing: antialiased; }
  .lp-nav { position: sticky; top: 0; z-index: 50; background: ${C.evergreen}; height: 68px; display: flex; align-items: center; justify-content: space-between; padding: 0 64px; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .lp-logo { display: flex; align-items: center; gap: 11px; cursor: pointer; background: none; border: none; padding: 0; margin: 0; font: inherit; text-align: left; }
  .lp-logo-name { font-family: 'Cormorant Garamond', serif; font-size: 24px; font-weight: 700; color: ${C.ivory}; letter-spacing: 0.02em; line-height: 1.1; }
  .lp-logo-sub { font-size: 9px; color: ${C.sage}; letter-spacing: 0.13em; text-transform: uppercase; margin-top: 2px; font-weight: 500; }
  .lp-nav-links { display: flex; align-items: center; gap: 30px; }
  .lp-nav-link { background: none; border: none; color: rgba(245,241,234,0.6); font-size: 13px; font-weight: 500; cursor: pointer; font-family: 'Manrope', sans-serif; letter-spacing: 0.02em; padding: 0; transition: color 0.15s; }
  .lp-nav-link:hover { color: ${C.ivory}; }
  .lp-nav-signin { background: transparent; color: ${C.ivory}; border: 1.5px solid ${C.brass}; border-radius: 50px; padding: 8px 22px; font-family: 'Manrope', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer; transition: background 0.15s, color 0.15s; display: flex; align-items: center; gap: 7px; }
  .lp-nav-signin:hover { background: ${C.brass}; color: ${C.evergreen}; }
  .lp-hero { background: ${C.ivory}; display: flex; flex-direction: column; align-items: center; text-align: center; padding: 100px 40px 0; }
  .lp-hero-eyebrow { display: inline-flex; align-items: center; gap: 8px; border: 1px solid ${C.brass}44; background: ${C.brass}0d; border-radius: 50px; padding: 6px 18px; font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: ${C.brass}; margin-bottom: 38px; font-weight: 600; }
  .lp-hero-eyebrow::before { content: '✦'; font-size: 9px; }
  .lp-h1 { font-family: 'DM Serif Display', serif; font-size: clamp(52px, 7.5vw, 90px); font-weight: 400; color: ${C.evergreen}; line-height: 1.04; letter-spacing: -0.01em; margin-bottom: 22px; }
  .lp-h1-em { color: ${C.sage}; font-style: italic; }
  .lp-hero-sub { font-size: 17px; color: #5a7060; max-width: 540px; line-height: 1.78; margin-bottom: 46px; font-weight: 400; }
  .lp-hero-btns { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; margin-bottom: 60px; }
  .lp-btn-primary { background: ${C.evergreen}; color: ${C.ivory}; border: none; border-radius: 50px; padding: 14px 38px; font-weight: 700; font-size: 13px; cursor: pointer; letter-spacing: 0.08em; text-transform: uppercase; font-family: 'Manrope', sans-serif; transition: background 0.15s; }
  .lp-btn-primary:hover { background: ${C.forest}; }
  .lp-btn-outline { background: transparent; color: ${C.evergreen}; border: 1.5px solid ${C.evergreen}; border-radius: 50px; padding: 14px 38px; font-weight: 600; font-size: 13px; cursor: pointer; font-family: 'Manrope', sans-serif; transition: background 0.15s; }
  .lp-btn-outline:hover { background: rgba(22,60,47,0.06); }
  .lp-stats { background: ${C.evergreen}; display: flex; justify-content: center; width: 100%; }
  .lp-stats-inner { display: flex; max-width: 680px; width: 100%; }
  .lp-stat { flex: 1; text-align: center; padding: 26px 20px; border-right: 1px solid rgba(245,241,234,0.1); }
  .lp-stat:last-child { border-right: none; }
  .lp-stat-val { font-family: 'DM Serif Display', serif; font-size: 28px; font-weight: 400; color: ${C.ivory}; line-height: 1; margin-bottom: 5px; }
  .lp-stat-lbl { font-size: 10px; color: ${C.sage}; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 600; }
  .lp-features { background: ${C.evergreen}; padding: 96px 64px; }
  .lp-eyebrow { font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase; color: ${C.sage}; font-weight: 600; display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
  .lp-eyebrow::before { content: ''; display: inline-block; width: 28px; height: 1px; background: ${C.sage}; }
  .lp-section-h2 { font-family: 'DM Serif Display', serif; font-size: clamp(34px, 4vw, 54px); font-weight: 400; color: ${C.ivory}; line-height: 1.1; margin-bottom: 56px; }
  .lp-section-h2 em { color: ${C.sage}; font-style: italic; }
  .lp-feat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px; max-width: 1100px; margin: 0 auto; }
  .lp-feat-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07); padding: 34px 28px; cursor: pointer; transition: background 0.18s, transform 0.18s, border-color 0.18s; border-radius: 3px; }
  .lp-feat-card:hover { background: rgba(127,185,142,0.1); transform: translateY(-3px); border-color: rgba(127,185,142,0.2); }
  .lp-feat-icon { width: 50px; height: 50px; background: rgba(176,141,87,0.12); border: 1px solid rgba(176,141,87,0.25); border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }
  .lp-feat-rule { width: 28px; height: 1px; background: ${C.brass}; opacity: 0.5; margin: 12px 0; }
  .lp-feat-title { font-family: 'DM Serif Display', serif; font-size: 19px; font-weight: 400; color: ${C.ivory}; margin-bottom: 9px; }
  .lp-feat-brief { font-size: 13px; color: rgba(245,241,234,0.5); line-height: 1.7; }
  .lp-feat-more { font-size: 11px; color: ${C.sage}; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 600; margin-top: 18px; opacity: 0.8; }
  .lp-about { background: ${C.ivory}; padding: 96px 64px; }
  .lp-about-inner { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: start; }
  .lp-about-h2 { font-family: 'DM Serif Display', serif; font-size: clamp(36px,4.5vw,58px); font-weight: 400; color: ${C.evergreen}; line-height: 1.1; margin-bottom: 22px; }
  .lp-about-h2 em { color: ${C.sage}; font-style: italic; }
  .lp-about-p { font-size: 15px; color: #4a5e4a; line-height: 1.85; margin-bottom: 16px; }
  .lp-about-cta { display: inline-flex; align-items: center; gap: 8px; color: ${C.brass}; font-size: 13px; font-weight: 700; letter-spacing: 0.04em; cursor: pointer; background: none; border: none; font-family: 'Manrope', sans-serif; padding: 0; margin-top: 6px; }
  .lp-stat-card { background: #fff; border: 1px solid #E8E4DC; border-radius: 14px; padding: 28px; margin-bottom: 14px; }
  .lp-stat-card-val { font-family: 'DM Serif Display', serif; font-size: 40px; font-weight: 400; color: ${C.evergreen}; line-height: 1; margin-bottom: 6px; }
  .lp-stat-card-val.brass { color: ${C.brass}; }
  .lp-stat-card-label { font-size: 13px; font-weight: 700; color: ${C.evergreen}; margin-bottom: 4px; }
  .lp-stat-card-sub { font-size: 13px; color: #9B9690; line-height: 1.6; }
  .lp-idea { background: ${C.forest}; padding: 96px 64px; }
  .lp-idea-inner { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 72px; align-items: start; }
  .lp-idea-h2 { font-family: 'DM Serif Display', serif; font-size: clamp(36px,4vw,52px); font-weight: 400; color: ${C.ivory}; line-height: 1.1; margin-bottom: 16px; }
  .lp-idea-sub { font-size: 15px; color: rgba(245,241,234,0.55); line-height: 1.75; }
  .lp-idea-input { width: 100%; padding: 12px 15px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.14); border-radius: 8px; color: ${C.ivory}; font-family: 'Manrope', sans-serif; font-size: 14px; outline: none; transition: border-color 0.15s; }
  .lp-idea-input::placeholder { color: rgba(245,241,234,0.28); }
  .lp-idea-input:focus { border-color: ${C.sage}; }
  .lp-idea-btn { width: 100%; padding: 13px 0; background: ${C.brass}; color: ${C.evergreen}; border: none; border-radius: 50px; font-family: 'Manrope', sans-serif; font-size: 13px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer; transition: background 0.15s; }
  .lp-idea-btn:hover { background: #c9a568; }
  .lp-idea-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .lp-footer { background: ${C.evergreen}; border-top: 1px solid rgba(255,255,255,0.07); padding: 38px 64px; display: flex; justify-content: space-between; align-items: center; }
  .lp-footer-logo { font-family: 'Cormorant Garamond', serif; font-size: 20px; font-weight: 700; color: ${C.ivory}; }
  .lp-footer-sub { font-size: 11px; color: rgba(245,241,234,0.3); margin-top: 3px; }
  .lp-footer-copy { font-size: 12px; color: rgba(245,241,234,0.28); }
  .lp-footer-signin { color: rgba(245,241,234,0.2); font-size: 11px; cursor: pointer; background: none; border: none; font-family: 'Manrope', sans-serif; transition: color 0.15s; }
  .lp-footer-signin:hover { color: rgba(245,241,234,0.5); }
  .lp-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.62); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 16px; }
  .lp-modal { background: #fff; border-radius: 16px; box-shadow: 0 16px 56px rgba(0,0,0,0.22); position: relative; }
  .lp-modal-close { position: absolute; top: 14px; right: 18px; background: none; border: none; font-size: 20px; color: #9B9690; cursor: pointer; line-height: 1; z-index: 1; }
  .lp-tab { flex: 1; padding: 11px; background: none; border: none; border-bottom: 2px solid transparent; font-family: 'Manrope', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; color: #9B9690; transition: color 0.15s, border-color 0.15s; }
  .lp-tab.active { color: ${C.evergreen}; border-bottom-color: ${C.evergreen}; }
  .lp-bullet { display: flex; align-items: flex-start; gap: 11px; font-size: 14px; color: ${C.charcoal}; line-height: 1.55; }
  .lp-bullet::before { content: ''; width: 7px; height: 7px; background: ${C.brass}; border-radius: 50%; flex-shrink: 0; margin-top: 6px; }
  @media (max-width: 900px) {
    .lp-nav { padding: 0 20px; }
    .lp-nav-link { display: none; }
    .lp-hero { padding: 72px 24px 0; }
    .lp-features { padding: 70px 20px; }
    .lp-feat-grid { grid-template-columns: 1fr; gap: 8px; }
    .lp-about { padding: 70px 20px; }
    .lp-about-inner { grid-template-columns: 1fr; gap: 48px; }
    .lp-idea { padding: 70px 20px; }
    .lp-idea-inner { grid-template-columns: 1fr; gap: 40px; }
    .lp-footer { padding: 28px 20px; flex-direction: column; gap: 12px; text-align: center; }
  }
`;

// ── Main component ─────────────────────────────────────────────────────────────
export default function LandingPage({ supabase }) {
  // Only modal-open flags live here — no form state, so typing never re-renders this component
  const [signInOpen,   setSignInOpen]   = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    return params.has("signin") || params.has("confirmed")
      || hash.includes("access_token") || hash.includes("type=signup");
  });
  const [activeModal,  setActiveModal]  = useState(null); // "features" | "about" | "idea" | feature.id
  const [idea,         setIdea]         = useState({ name: "", email: "", message: "" });
  const [ideaDone,     setIdeaDone]     = useState(false);
  const [ideaLoading,  setIdeaLoading]  = useState(false);
  const [ideaErr,      setIdeaErr]      = useState("");

  const closeModal   = () => setActiveModal(null);
  const openSignIn   = () => setSignInOpen(true);
  const featPopup    = FEATURES.find(f => f.id === activeModal);

  // Logo click: already on the landing page, so just scroll to top and drop
  // any hash (e.g. /#contact) rather than navigating anywhere.
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  };

  const submitIdea = async (e) => {
    e.preventDefault();
    setIdeaLoading(true); setIdeaErr("");
    try {
      await submitIdeaToBackend(idea);
      setIdeaDone(true);
    } catch {
      setIdeaErr("Something went wrong. Please try again.");
    } finally {
      setIdeaLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: "'Manrope',sans-serif", background: C.ivory, minHeight: "100vh" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600;1,700&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{CSS}</style>

      {/* Modals — all module-level, stable identity across renders */}
      {signInOpen && <SignInModal supabase={supabase} onClose={() => setSignInOpen(false)} />}
      {activeModal === "features" && <FeaturesModal onClose={closeModal} onOpenFeature={setActiveModal} />}
      {activeModal === "about"    && <AboutModal    onClose={closeModal} onGetStarted={() => { closeModal(); openSignIn(); }} />}
      {activeModal === "idea"     && <IdeaModal     onClose={closeModal} />}
      {featPopup                  && <FeatureModal  f={featPopup} onClose={closeModal} onGetStarted={() => { closeModal(); openSignIn(); }} />}

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav className="lp-nav">
        <button type="button" className="lp-logo" onClick={scrollToTop} aria-label="Leasly — back to top">
          <img src="/Leasly-logofinal.png" alt="Leasly" width="32" height="32" style={{ borderRadius: 6, objectFit: "contain" }} />
          <div>
            <div className="lp-logo-name">Leasly</div>
            <div className="lp-logo-sub">by Gibbs Prodigy Group</div>
          </div>
        </button>
        <div className="lp-nav-links">
          <a href="/features" className="lp-nav-link" style={{ textDecoration: "none" }}>Features</a>
          <button className="lp-nav-link" onClick={() => setActiveModal("about")}>About</button>
          <a href="/pricing" className="lp-nav-link" style={{ textDecoration: "none" }}>Pricing</a>
          <button className="lp-nav-link" onClick={() => setActiveModal("idea")}>Share an Idea</button>
          <button className="lp-nav-signin" onClick={openSignIn}>
            <span style={{ fontSize: 11, color: C.brass }}>✦</span> Sign In
          </button>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-eyebrow">Property Management, Reimagined</div>
        <h1 className="lp-h1">
          Rent collected.<br />
          Taxes done.<br />
          <span style={{ fontStyle: "italic", color: C.brass }}>Your weekends back.</span>
        </h1>
        <p className="lp-hero-sub">
          Leasly is the all-in-one property management platform built for modern landlords — from single units to full portfolios. Built on legacy, powered by technology.
        </p>
        <div className="lp-hero-btns">
          <button className="lp-btn-primary" onClick={openSignIn}>Get Started</button>
          <button className="lp-btn-outline" onClick={() => setActiveModal("idea")}>Share an Idea</button>
        </div>
        <div className="lp-stats">
          <div className="lp-stats-inner">
            {[["100%", "Cloud-Based"], ["Real-Time", "Portfolio Tracking"], ["All-In-One", "Landlord Platform"]].map(([v, l]) => (
              <div key={l} className="lp-stat">
                <div className="lp-stat-val">{v}</div>
                <div className="lp-stat-lbl">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────── */}
      <section className="lp-features" id="features">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="lp-eyebrow">What We Offer</div>
          <h2 className="lp-section-h2">Every tool a landlord<br /><em>actually needs.</em></h2>
          <div className="lp-feat-grid">
            {FEATURES.map(f => (
              <div key={f.id} className="lp-feat-card" onClick={() => setActiveModal(f.id)}>
                <div className="lp-feat-icon">{f.icon}</div>
                <div className="lp-feat-rule" />
                <div className="lp-feat-title">{f.title}</div>
                <p className="lp-feat-brief">{f.brief}</p>
                <div className="lp-feat-more">Learn more →</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About ───────────────────────────────────────────────────── */}
      <section className="lp-about" id="about">
        <div className="lp-about-inner">
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: C.brass, fontWeight: 600, display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <span style={{ display: "inline-block", width: 28, height: 1, background: C.brass }} />Our Story
            </div>
            <h2 className="lp-about-h2">Built on legacy,<br /><em>powered by technology.</em></h2>
            <p className="lp-about-p">Gibbs Prodigy Group is a real estate property management company built on the belief that smart technology and genuine relationships create better communities.</p>
            <p className="lp-about-p">Leasly started as our internal platform — the tool we wished existed when managing our own portfolio. Built to solve real problems, now available to landlords everywhere.</p>
            <button className="lp-about-cta" onClick={openSignIn}>Get started today →</button>
          </div>
          <div>
            <div className="lp-stat-card">
              <div className="lp-stat-card-val">100%</div>
              <div className="lp-stat-card-label">Cloud-Based</div>
              <div className="lp-stat-card-sub">Access your portfolio from any device, anywhere, at any time. No software to install.</div>
            </div>
            <div className="lp-stat-card">
              <div className="lp-stat-card-val brass">Family Built</div>
              <div className="lp-stat-card-label" style={{ color: C.brass }}>Gibbs Prodigy Group</div>
              <div className="lp-stat-card-sub">Rooted in Pittsburg, CA. Built by landlords, for landlords — with the care that only comes from doing this ourselves.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Have an idea? ────────────────────────────────────────────── */}
      <section className="lp-idea" id="contact">
        <div className="lp-idea-inner">
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(127,185,142,0.7)", fontWeight: 600, display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
              <span style={{ display: "inline-block", width: 28, height: 1, background: "rgba(127,185,142,0.5)" }} />Get in Touch
            </div>
            <h2 className="lp-idea-h2">Have an idea?</h2>
            <p className="lp-idea-sub">Feature request, feedback, or just want to say hello — we read everything and build what our users actually need.</p>
            <div style={{ marginTop: 32, paddingTop: 32, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
              <div style={{ fontSize: 12, color: "rgba(245,241,234,0.35)", marginBottom: 6 }}>Direct contact</div>
              <a href="mailto:support@leaslypro.com" style={{ color: C.sage, fontSize: 14, fontWeight: 600, textDecoration: "none" }}>support@leaslypro.com</a>
            </div>
          </div>
          <div>
            {ideaDone ? (
              <div style={{ background: "rgba(127,185,142,0.1)", border: "1px solid rgba(127,185,142,0.25)", borderRadius: 14, padding: "40px 30px", textAlign: "center" }}>
                <div style={{ fontSize: 40, marginBottom: 14, color: C.sage }}>✓</div>
                <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 24, color: C.ivory, marginBottom: 10 }}>Got it — thank you!</div>
                <div style={{ fontSize: 14, color: "rgba(245,241,234,0.55)", lineHeight: 1.7 }}>We'll get back to you at {idea.email}.</div>
              </div>
            ) : (
              <form onSubmit={submitIdea} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "rgba(245,241,234,0.55)", display: "block", marginBottom: 5 }}>Name</label>
                    <input className="lp-idea-input" required value={idea.name} onChange={e => setIdea(s => ({ ...s, name: e.target.value }))} placeholder="Your name" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "rgba(245,241,234,0.55)", display: "block", marginBottom: 5 }}>Email</label>
                    <input className="lp-idea-input" type="email" required value={idea.email} onChange={e => setIdea(s => ({ ...s, email: e.target.value }))} placeholder="you@example.com" />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "rgba(245,241,234,0.55)", display: "block", marginBottom: 5 }}>Message</label>
                  <textarea className="lp-idea-input" required rows={5} value={idea.message} onChange={e => setIdea(s => ({ ...s, message: e.target.value }))} placeholder="Tell us your idea, feature request, or feedback…" style={{ resize: "vertical" }} />
                </div>
                {ideaErr && <ErrBox msg={ideaErr} />}
                <button type="submit" className="lp-idea-btn" disabled={ideaLoading}>{ideaLoading ? "Sending…" : "Send Message"}</button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div>
          <div className="lp-footer-logo">Leasly</div>
          <div className="lp-footer-sub">by Gibbs Prodigy Group · Pittsburg, CA</div>
        </div>
        <div className="lp-footer-copy">© 2026 Gibbs Prodigy Group. All rights reserved.</div>
        <button className="lp-footer-signin" onClick={openSignIn}>Sign in</button>
      </footer>
    </div>
  );
}
